import { useCallback, useState } from "react";
import publicAxios from "../../utils/publicAxios";
import {
  getSendEmailOtpURL,
  getVerifyEmailOtpURL
} from "../../utils/constants/apiEndPoints";
import { OTP_CODE_LENGTH } from "../../utils/constants/appConstants";
import { signup } from "../../locales/en";
import {
  getStoredUTMParams,
  buildHubspotFormContext
} from "../../utils/hubspotUtils";

/**
 * The verification step of signup: send a code to the address on the form,
 * then confirm it.
 *
 * The step opens only once the signup form has been filled in and submitted,
 * and only for the addresses the server says owe a code — it answers the send
 * with `verificationRequired`, so the domains that are exempt live in one place
 * on the server rather than being guessed here. This hook owns the exchange
 * with the inbox and nothing else: the caller still holds the form, and decides
 * what happens once the address is settled, which is when the account may be
 * created.
 */
const useSignupEmailOtp = () => {
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [resent, setResent] = useState("");

  const copy = signup?.emailOtp ?? {};

  /** Pulls the server message out of an axios error, with a fallback. */
  const messageFrom = (apiError, fallback) =>
    apiError?.response?.data?.message ||
    apiError?.response?.data?.errors?.[0] ||
    fallback;

  /**
   * Requests a code and, on success, opens the step.
   *
   * The whole identity from the form goes with the request, not just the
   * address: the server makes this signup's CRM records as the code goes out,
   * so they read the same as the account will.
   *
   * @param {{ email: string, firstName?: string, lastName?: string,
   *   phoneNumber?: string, referralCode?: string }} signupDetails the
   *   identity typed into the form
   * @param {{ isResend?: boolean }} options whether this replaces a code already sent
   * @returns {Promise<{ ok: boolean, verificationRequired: boolean }>} whether
   *   the address was accepted, and whether a code went out for it
   */
  const sendCode = useCallback(
    async (signupDetails, { isResend = false } = {}) => {
      const { email, firstName, lastName, phoneNumber, referralCode } =
        signupDetails ?? {};
      const address = String(email ?? "").trim();
      setSending(true);
      setError("");
      setResent("");
      try {
        const utm = getStoredUTMParams();
        const response = await publicAxios.post(getSendEmailOtpURL, {
          email: address,
          firstName: firstName || null,
          lastName: lastName || null,
          primaryContactNumber: phoneNumber || null,
          referralCode: referralCode || null,
          utmSource: utm.utmSource || null,
          utmMedium: utm.utmMedium || null,
          utmCampaign: utm.utmCampaign || null,
          utmTerm: utm.utmTerm || null,
          utmContent: utm.utmContent || null,
          utmMarket: utm.utmMarket || null,
          ...buildHubspotFormContext()
        });
        // Absent flag means a code went out: an older server sends one to every
        // address, and the step is what it expects next.
        const verificationRequired =
          response?.data?.data?.verificationRequired !== false;
        setCode("");
        setCodeSent(verificationRequired);
        if (isResend && verificationRequired) {
          setResent(copy.resent);
        }
        return { ok: true, verificationRequired };
      } catch (apiError) {
        setError(messageFrom(apiError, copy.sendFailed));
        return { ok: false, verificationRequired: false };
      } finally {
        setSending(false);
      }
    },
    [copy.resent, copy.sendFailed]
  );

  /**
   * Submits the typed code. A wrong code leaves the customer on the step with
   * the server's message, which says how many attempts remain.
   *
   * @param {string} email the address being verified
   * @returns {Promise<boolean>} whether the address is now verified
   */
  const verifyCode = useCallback(
    async (email) => {
      const address = String(email ?? "").trim();
      if (code.length !== OTP_CODE_LENGTH) {
        setError(copy.required);
        return false;
      }

      setVerifying(true);
      setError("");
      setResent("");
      try {
        await publicAxios.post(getVerifyEmailOtpURL, {
          email: address,
          code: Number(code)
        });
        return true;
      } catch (apiError) {
        setError(messageFrom(apiError, copy.verifyFailed));
        return false;
      } finally {
        setVerifying(false);
      }
    },
    [code, copy.required, copy.verifyFailed]
  );

  /**
   * Closes the step and goes back to the form, dropping the code entered
   * against the old address so a returning customer is not shown boxes that
   * could only ever be rejected.
   */
  const reset = useCallback(() => {
    setCodeSent(false);
    setCode("");
    setError("");
    setResent("");
  }, []);

  return {
    codeSent,
    code,
    setCode,
    sending,
    verifying,
    error,
    setError,
    resent,
    sendCode,
    verifyCode,
    reset
  };
};

export default useSignupEmailOtp;
